import React from "react";
import { Box, Typography, Container, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import heroDoctorImage from "../assets/Gemini_Generated_Image_5i6d9d5i6d9d5i6d.png";

const HeroContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  minHeight: "90vh",
  position: "relative",
  display: "flex",
  alignItems: "center",
  backgroundColor: "transparent",
  overflow: "hidden",
  padding: { xs: "40px 0", md: "60px 0" },
  [theme.breakpoints.down("md")]: {
    minHeight: "auto",
  },
}));

// Left Section - Image/Cards Area
const LeftSection = styled(Box)(({ theme }) => ({
  width: "60%",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: { xs: "20px", md: "40px 60px" },
  background: "transparent",
  overflow: { xs: "hidden", md: "visible" },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    padding: "40px 20px",
    minHeight: "400px",
  },
}));

// Right Section - Dark Text Area
const RightSection = styled(Box)(({ theme }) => ({
  width: "40%",
  position: "relative",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: { xs: "40px 30px", md: "60px 80px" },
  background: "transparent",
  zIndex: 1,
  [theme.breakpoints.down("md")]: {
    width: "100%",
    padding: "40px 30px",
    minHeight: "auto",
  },
}));

// Image Wrapper - Contains image and floating cards
const ImageWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  maxWidth: "500px",
  height: { xs: "350px", sm: "450px", md: "550px" },
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
  overflow: "visible",
  [theme.breakpoints.down("md")]: {
    maxWidth: "100%",
    padding: { xs: "0 20px", sm: "0 30px" },
  },
}));

// Main Image Container
const ImageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  borderRadius: "20px 500px 500px 20px",
  overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
  zIndex: 1,
  "&::after": {
    content: '""',
    position: "absolute",
    right: "-25px",
    top: 0,
    bottom: 0,
    width: "50px",
    background: "transparent",
    borderRadius: "0 24px 24px 0",
    zIndex: -1,
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
}));

// Floating Card Component - Contained within ImageWrapper
const FloatingCard = styled(motion.div)(({ theme }) => ({
  position: "absolute",
  width: { xs: "140px", sm: "160px", md: "180px" },
  height: { xs: "180px", sm: "210px", md: "240px" },
  background: "rgba(255, 255, 255, 0.95)",
  borderRadius: "16px",
  boxShadow: "0 12px 40px rgba(70, 116, 217, 0.25)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: { xs: "12px", md: "16px" },
  cursor: "pointer",
  border: "1px solid rgba(70, 116, 217, 0.1)",
  zIndex: 2,
  [theme.breakpoints.down("sm")]: {
    width: "120px",
    height: "160px",
    padding: "10px",
  },
}));

const HeroSection = () => {
  return (
    <HeroContainer>
      <Container maxWidth="xl" sx={{ width: "100%", position: "relative", zIndex: 1 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={0}
          sx={{ width: "100%", alignItems: "stretch" }}
        >
          {/* Left Section - Image with Floating Cards */}
          <LeftSection>
            <ImageWrapper>
              {/* Main Image */}
              <ImageContainer
                component={motion.div}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
              >
                <Box
                  component="img"
                  src={heroDoctorImage}
                  alt="Smart Health Report"
                  sx={{
                    width: "100%",
                    height: { xs: "290px", sm: "400px", md: "500px" },
                    objectFit: "cover",
                    objectPosition: "center",
                    display: "block",
                  }}
                />
              </ImageContainer>
            </ImageWrapper>
          </LeftSection>

          {/* Right Section - Text Content */}
          <RightSection>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {/* Badge/Label */}
              <Typography
                component={motion.div}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                sx={{
                  display: "inline-block",
                  color: "#4673D9",
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "10px", sm: "11px", md: "12px" },
                  fontWeight: 600,
                  mb: { xs: 2, md: 2.5 },
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                  padding: { xs: "6px 12px", md: "8px 16px" },
                  backgroundColor: "rgba(70, 115, 217, 0.08)",
                  borderRadius: "6px",
                  border: "1px solid rgba(70, 115, 217, 0.15)",
                }}
              >
                Why Smart Reporting Matters
              </Typography>

              {/* Main Heading */}
              <Typography
                component={motion.h1}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                sx={{
                  background: "linear-gradient(135deg, #1D47A4 0%, #4673D9 50%, #6E91EB 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: { xs: "32px", sm: "42px", md: "56px" },
                  fontWeight: 700,
                  mb: { xs: 2, md: 3 },
                  lineHeight: { xs: "1.2", md: "1.1" },
                  letterSpacing: { xs: "-0.02em", md: "-0.03em" },
                  textShadow: "0 2px 20px rgba(29, 71, 164, 0.1)",
                }}
              >
                Smart Health Report
              </Typography>

              {/* Description */}
              <Typography
                component={motion.p}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                sx={{
                  color: "#4B5563",
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "15px", sm: "16px", md: "18px" },
                  fontWeight: 400,
                  lineHeight: { xs: "26px", md: "32px" },
                  maxWidth: "580px",
                  mb: { xs: 0, md: 0 },
                  letterSpacing: "-0.01em",
                }}
              >
                Traditional lab reports are overloaded with medical numbers that
                patients struggle to understand and clinicians spend time
                explaining. This leads to delays in care, confusion, and low
                patient engagement.{" "}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: "#1D47A4",
                    background: "linear-gradient(135deg, #1D47A4 0%, #4673D9 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Smart Health Reports
                </Box>{" "}
                convert raw medical results into simplified, visual, and actionable insights -
                enabling clarity, faster decisions, and better patient outcomes.
              </Typography>
            </motion.div>
          </RightSection>
        </Stack>
      </Container>
    </HeroContainer>
  );
};

export default HeroSection;
